import React from 'react'
import { LayoutProps } from 'src/layouts/types'
import useResponsive from 'src/hooks/useResponsive'
import ScrollToTop from 'src/components/ui/scroll-to-top'

import Navigation from './components/vertical/navigation'
import GlobalSearchComponent from './components/GlobalSearchComponent'

import Fab from '@mui/material/Fab'
import Stack from '@mui/material/Stack'
import Icon from 'src/components/ui/icon'
import { styled } from '@mui/material/styles'
import { useSearchQuery } from 'src/hooks/useSearchQuery'
import { SearchAsyncModal } from 'src/modals/SearchAsyncModal'
import { useSelectedAccounts } from 'src/hooks/useSelectedAccounts'
import { useAccountSelect } from 'src/hooks/useAccount'
import { AssinstantWindow } from 'src/components/widgets/Assistant'
import { useAssistantSelect } from 'src/hooks/useAssistant'

const LayoutContext = React.createContext<{
  navState: boolean
  setNavHandle?: () => void
}>({ navState: false })

export const useLayoutContext = () => React.useContext(LayoutContext)
export const drawerWidth = 80

const Main = styled('main')<{
  open?: boolean
  isMobile: boolean
  modalOpened?: boolean
}>(({ theme, open, modalOpened, isMobile }) => ({
  width: '100vw',
  height: '100vh',

  display: 'flex',
  flexDirection: 'column',

  transform: modalOpened ? 'scale(0.98)' : 'scale(1)',

  transition: open
    ? theme.transitions.create(['margin', 'transform'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      })
    : theme.transitions.create(['margin', 'transform'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
  marginLeft: !isMobile ? `${drawerWidth}px` : 0,
}))

const VerticalLayout = (props: LayoutProps) => {
  const { query } = useSearchQuery()
  const { children, scrollToTop } = props
  const [navState, setNavState] = React.useState(false)
  const { isMobile } = useResponsive()
  const modalOpened = Boolean(query.modal)
  const { initialize } = useSelectedAccounts()
  const isMobileWebView = useAccountSelect.isMobileWebView()
  const windowOpenRightSideBar = useAssistantSelect.windowOpenRightSideBar()
  const setNavHandle = () => {
    setNavState((prev) => !prev)
  }

  React.useEffect(() => {
    if (!isMobileWebView) {
      initialize()
    }
  }, [isMobileWebView])

  return (
    <>
      <LayoutContext.Provider value={{ navState, setNavHandle }}>
        <Stack
          direction="row"
          sx={{
            height: '100%',
            width: '100%',
            paddingRight: windowOpenRightSideBar ? '375px' : 0,
          }}
        >
          <SearchAsyncModal />
          <AssinstantWindow />
          <Navigation />

          <Main open={navState} isMobile={isMobile} modalOpened={modalOpened}>
            {!isMobileWebView && (
              <Stack
                pt={{ sm: 3, xl: 6 }}
                sx={{
                  marginLeft: (theme) => ({
                    xs: theme.spacing(3),
                    md: theme.spacing(6),
                  }),
                  marginRight: (theme) => ({
                    xs: theme.spacing(3),
                    md: theme.spacing(6),
                  }),
                }}
              >
                <GlobalSearchComponent />
              </Stack>
            )}

            <Stack
              pt={{ xs: 5, xl: 6 }}
              flex={1}
              direction="column"
              sx={{
                transition: 'padding .25s ease-in-out',
              }}
            >
              {children}
            </Stack>
          </Main>
        </Stack>
      </LayoutContext.Provider>

      {scrollToTop ? (
        scrollToTop(props)
      ) : (
        <ScrollToTop className="mui-fixed">
          <Fab color="primary" size="small" aria-label="scroll back to top">
            <Icon icon="tabler:arrow-up" />
          </Fab>
        </ScrollToTop>
      )}
    </>
  )
}

export default VerticalLayout
